import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import "../styles/Login.css";

// ✅ Import Gradient Bold Logo
import { ReactComponent as GradientBoldLogo } from "../assets/Transparent svg/Bold Logo with Text/Gradient Bold Logo.svg";
import { googleLogin } from "../auth/authService";



const Login = () => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({ email: false, password: false });
  const [isActive, setIsActive] = useState({ email: false, password: false });
  const [showResend, setShowResend] = useState(false);

  const navigate = useNavigate();

  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const hasAccount = localStorage.getItem("hasAccount");
    if (token || hasAccount) {
      setIsLoggedIn(true);
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("hasAccount");
    localStorage.removeItem("guest");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("isGoogleUser");
    alert("You’ve been logged out.");
    setIsLoggedIn(false);
    navigate("/login");
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: false });
  };

  const handleFocus = (e) => {
    setIsActive({ ...isActive, [e.target.name]: true });
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setIsActive({ ...isActive, [name]: false });

    if (!value.trim()) {
      setErrors({ ...errors, [name]: true });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {
      email: !formData.email,
      password: !formData.password,
    };
    setErrors(newErrors);

    if (!newErrors.email && !newErrors.password) {
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/auth/login`,
          formData
        );
        const { token, user } = res.data;

        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("hasAccount", "true");
        localStorage.setItem("isGoogleUser", "false");

        alert("🎉 Login successful!");
        navigate("/dispatch-history");
      } catch (error) {
        const message = error.response?.data?.error || "Login failed. Please try again.";
        if (message.toLowerCase().includes("verify")) {
          setShowResend(true);
        }
        alert(message);
      }
    }
  };


  const handleGoogleSignIn = async (response) => {
    const token = response.credential;
    try {
      const data = await googleLogin(token);

      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      localStorage.setItem("hasAccount", "true");
      localStorage.setItem("isGoogleUser", "true");
      alert("🎉 Login successful!");
      navigate("/dispatch-history");
    } catch (err) {
      alert(err.message || "Google login failed");
    }
  };

  useEffect(() => {
    const renderGoogleBtn = () => {
      const googleDiv = document.getElementById("google-btn");

      // 🔒 Safe-guard: skip if SDK not ready or element missing
      if (!window.google || !window.google.accounts || !googleDiv) return;

      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        callback: handleGoogleSignIn,
      });
      window.google.accounts.id.renderButton(googleDiv, {
        theme: "outline",
        size: "large",
        width: 300,
      });
    };

    // Small delay ensures SDK loads before we call it
    const timeout = setTimeout(renderGoogleBtn, 300);

    window.addEventListener("resize", renderGoogleBtn);
    return () => {
      clearTimeout(timeout);
      window.removeEventListener("resize", renderGoogleBtn);
    };
  }, []);


  const handleResend = async () => {
    const email = formData.email;
    if (!email) return alert("Enter a valid email first.");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/resend-verification`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );
      const data = await res.json();
      alert(data.message || data.error);
    } catch (err) {
      alert("Something went wrong");
    }
  };

  return (
    <div className="login-container">
      <div className="login-left">
        <img src="/log-img.png" alt="Login" className="login-image" />
        <img src="/star-icon.svg" alt="Star Icon" className="star-icon star-icon-top" />
        <img src="/blue-star-icon.svg" alt="Star Icon" className="star-icon star-icon-bottom" />
        <div className="white-circle"></div>

        <div className="login-badge track">
          <img src="/track-icon.svg" alt="Track Icon" />
          <p>Track & Manage <br />documents on the go!</p>
        </div>
        <div className="white-circle-bottom"></div>
        <div className="white-circle-bottom-right"></div>
        <div className="white-circle-bottom-right-up"></div>
        <div className="login-badge speed">
          <img src="/speed-icon.svg" alt="Speed Icon" />
          <p>Gain speed with <br />your document delivery 🚀</p>
        </div>
        <div className="login-badge dispatch">
          <img src="/dispatch-icon.svg" alt="Dispatch Icon" />
          <p>Dispatch your Cheques <br />from anywhere</p>
        </div>
      </div>

      <div className="login-right">
        <div className="login-header">
          <GradientBoldLogo className="login-logo-svg" />
          <h2 className="login-welcome">Welcome back <span role="img" aria-label="hugging face">🤗</span></h2>
        </div>

        <div className="google-btn-wrapper">
          <div id="google-btn"></div>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          <div className={`login-input-group ${isActive.email ? "active" : ""} ${formData.email ? "filled" : ""} ${errors.email ? "error" : ""}`}>
            <img src="/mail-icon.svg" alt="Email Icon" className="input-icon" />
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              required
            />
          </div>
          {errors.email && <p className="error-text">Email is required</p>}

          <div className={`login-input-group ${isActive.password ? "active" : ""} ${formData.password ? "filled" : ""} ${errors.password ? "error" : ""}`}>
            <img src="/lock-icon.svg" alt="Password Icon" className="input-icon" />
            <input
              type="password"
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              required
            />
          </div>
          {errors.password && <p className="error-text">Password is required</p>}

          <a href="/forgot-password" className="login-forgot-password">Forgot password?</a>
          {isLoggedIn ? (
            <button
              type="button"
              onClick={handleLogout}
              className="login-btn"
              style={{
                backgroundColor: "#fff",
                color: "#e53935",
                border: "1.5px solid #e53935",
                fontWeight: "600",
              }}
            >
              Sign Out
            </button>
          ) : (
            <button type="submit" className="login-btn">
              Log In
            </button>
          )}

          {/* <button type="submit" className="login-btn">Log in</button> */}
          {/* {!isLoggedIn ? (
            <button type="submit" className="login-btn">Log in</button>
          ) : (
            <button
              type="button"
              className="logout-btn"
              onClick={handleLogout}
              style={{
                backgroundColor: "#ccc",
                cursor: "pointer",
                color: "#333",
                fontWeight: "600",
              }}
            >
              Log out
            </button>
          )} */}




        </form>

        {/* Hide resend for Google users */}
        {showResend && !JSON.parse(localStorage.getItem("isGoogleUser") || "false") && (
          <button className="resend-verification-btn" onClick={handleResend}>
            ✉️ Resend Verification Email
          </button>
        )}

        <div className="login-divider">
          <span></span>
          <p>OR</p>
          <span></span>
        </div>

        {/* <p className="login-disclaimer">
          Don’t have an account?
          <span className="sign-up-link" onClick={() => navigate("/signup")}> Sign up</span>
        </p> */}
        {/* <p className="login-disclaimer">
          Don’t have an account?
          <span
            className={`sign-up-link ${isLoggedIn ? "disabled" : ""}`}
            onClick={() => !isLoggedIn && navigate("/signup")}
          >
            {isLoggedIn ? " Already Signed Up" : " Sign up"}
          </span>
        </p> */}

        <p className="login-disclaimer">
          Don’t have an account?
          <span className="sign-up-link" onClick={() => navigate("/signup")}>
            Sign up
          </span>
        </p>
        <div style={{ textAlign: "center", marginTop: "1rem" }}>
          <button
            onClick={() => {
              localStorage.setItem("guest", "true");
              localStorage.setItem("token", "guest-temp-token");
              window.location.href = "/dashboard";
            }}
            className="guest-dispatch-btn"
            style={{
              background: "transparent",
              border: "1px solid #ccc",
              borderRadius: "6px",
              padding: "10px 14px",
              width: "100%",
              color: "#444",
              cursor: "pointer",
            }}
          >
            🚀 Dispatch as Guest
          </button>
          <p style={{ fontSize: "13px", color: "#777", marginTop: "6px" }}>
            You can still receive your confirmation by email.
          </p>
        </div>

      </div>
    </div>
  );
};



export default Login;
